/**
 * Samples screen logic (F5.6-m/n): labels and the "Send to lab" form, validated like uc-core's
 * DispatchSampleDto (labOrgId UUID; panelRequested non-empty ≤ 60; sentAt ISO date;
 * dispatchRef optional ≤ 40). The server only sends SEALED / IN_STORE samples (409 otherwise)
 * and only to an org of kind LAB (422).
 */
import { humanize } from '../labels'

export const SAMPLE_KINDS = ['LOT_RETAINED', 'BATCH_QC'] as const
export const SAMPLE_KIND_LABEL: Record<string, string> = {
  LOT_RETAINED: 'Lot sample (gate)',
  BATCH_QC: 'Batch check',
  SOIL: 'Soil',
  RESIDUE: 'Residue',
}
export const sampleKindLabel = (k: string) => SAMPLE_KIND_LABEL[k] ?? humanize(k)

export const SAMPLE_STATUSES = ['SEALED', 'IN_STORE', 'SENT_TO_LAB', 'TESTED', 'DISPOSED'] as const
export const SAMPLE_STATUS_LABEL: Record<string, string> = {
  SEALED: 'Sealed',
  IN_STORE: 'In the sample store',
  SENT_TO_LAB: 'Sent to lab',
  TESTED: 'Tested',
  DISPOSED: 'Disposed',
}

/** Statuses the server accepts for dispatch. */
export const canSendToLab = (status: string) => status === 'SEALED' || status === 'IN_STORE'

export interface DispatchForm {
  labOrgId: string
  panelRequested: string
  /** yyyy-mm-dd (IST day the sample left). */
  sentDate: string
  dispatchRef: string
}
export type DispatchErrors = Partial<Record<keyof DispatchForm, string>>

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** `today` is the IST date key (yyyy-mm-dd); a send date in the future is refused. */
export function validateDispatch(f: DispatchForm, today: string): DispatchErrors {
  const e: DispatchErrors = {}
  if (!UUID.test(f.labOrgId)) e.labOrgId = 'Pick the lab'
  const panel = f.panelRequested.trim()
  if (!panel) e.panelRequested = 'Say which tests are asked for'
  else if (panel.length > 60) e.panelRequested = 'At most 60 characters'
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.sentDate)) e.sentDate = 'Pick the date it was sent'
  else if (f.sentDate > today) e.sentDate = 'Cannot be in the future'
  if (f.dispatchRef.trim().length > 40) e.dispatchRef = 'At most 40 characters'
  return e
}

/**
 * Form → request body. The sent date is stamped at IST noon of that day (or the current time
 * when it is today) so the event never lands on the wrong IST day.
 */
export function toDispatchBody(
  f: DispatchForm,
  today: string,
  now: Date = new Date(),
): { labOrgId: string; panelRequested: string; sentAt: string; dispatchRef?: string } {
  const sentAt = f.sentDate === today ? now.toISOString() : `${f.sentDate}T12:00:00+05:30`
  const ref = f.dispatchRef.trim()
  return {
    labOrgId: f.labOrgId,
    panelRequested: f.panelRequested.trim(),
    sentAt,
    ...(ref ? { dispatchRef: ref } : {}),
  }
}

/** The server's refusal in words for the dialog: 409 not sealed, 422 not a lab. */
export function dispatchErrorMessage(e: { status?: number; message?: string } | null | undefined): string {
  if (!e) return ''
  if (e.status === 409) return 'This sample is no longer sealed — it may already have been sent. Refresh the list.'
  if (e.status === 422) return 'That organisation is not registered as a lab.'
  if (e.status === 404) return 'This sample no longer exists.'
  return e.message || 'Could not record the dispatch.'
}
